import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ErrorPage } from "../error/ErrorPage";
import { Proposition } from "../../utils/types";
import {
  findPropositionByUuid,
  findUserTokenByUserEmail,
} from "../../database/queries/queries";
import { setProposalApproval } from "../../database/queries/commands";
import { StatusMessage } from "../../components/StatusMessage";
import { sendNotification } from "../../database/config/firebase.config";
import { Loading } from "../../components/Loading";
import { useGoogleAuthContext } from "../../contexts/GoogleAuthContext";
import { Info, Place, Today } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";

export const PropositionEvaluation = () => {
  const { uuid: urlId } = useParams();

  const [proposition, setProposition] = useState<Proposition | undefined>();
  const [isVoteDialogOpen, setVoteDialogOpen] = useState(false);
  const [isVoteErrorDialogOpen, setVoteErrorDialogOpen] = useState(false);
  const [isApprove, setApprove] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [token, setToken] = useState("");

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { user } = useGoogleAuthContext();

  useEffect(() => {
    setLoading(true);
    findPropositionByUuid(urlId!).then((prop) => {
      setProposition(prop);
      setLoading(false);
    });
  }, [urlId]);

  if (!proposition) {
    return <ErrorPage />;
  }

  if (isLoading) {
    return <Loading />;
  }

  function handleVoteYes() {
    findUserTokenByUserEmail(proposition?.createdBy!).then((result) => {
      if (result) {
        setToken(result.token);
      }
      setVoteDialogOpen(true);
      setApprove(true);
    });
  }

  function handleVoteNo() {
    setVoteDialogOpen(true);
    setApprove(false);
  }

  const handleToggleDialog = () => {
    setVoteDialogOpen(!isVoteDialogOpen);
  };

  const confirmVote = async () => {
    const newUuid = await setProposalApproval(
      proposition.id,
      isApprove,
      user?.email
    );
    if (newUuid) {
      setVoteDialogOpen(false);
      sendNotification(
        `Sua proposta #${newUuid} foi aprovada`,
        "Compartilhe-a nas redes sociais",
        token,
        user!
      );
      queryClient.invalidateQueries({ queryKey: ["propositions"] });
      navigate("/propostas");
    } else {
      setVoteDialogOpen(false);
      setVoteErrorDialogOpen(true);
    }
    console.log(newUuid);
  };

  const DialogText = () => {
    let voteTitle;
    let voteDescription;

    switch (proposition.status) {
      case "SENT":
        if (isApprove) {
          voteTitle = `Tem certeza que deseja votar "SIM"?`;
          voteDescription =
            'Após clicar em "CONFIRMA", esta proposta será VALIDADA e incluída no aplicativo';
        } else {
          voteTitle = `Tem certeza que deseja votar "NÃO"?`;
          voteDescription =
            'Após clicar em "CONFIRMA", esta proposta será REJEITADA e NÃO irá aparecer no aplicativo';
        }
        break;
      default:
        voteTitle = `Tem certeza que deseja alterar a sua avaliação?`;
        if (isApprove) {
          voteDescription =
            'Após clicar em "CONFIRMA", esta proposta será VALIDADA e incluída no aplicativo';
        } else {
          voteDescription =
            'Após clicar em "CONFIRMA", esta proposta será REJEITADA e NÃO irá aparecer no aplicativo';
        }
        break;
    }
    return (
      <>
        <DialogTitle id="alert-vote-dialog-title">{voteTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-vote-dialog-description">
            {voteDescription}
          </DialogContentText>
        </DialogContent>
      </>
    );
  };
  const AlertDialog = () => {
    return (
      <>
        <Dialog
          open={isVoteDialogOpen}
          onClose={handleToggleDialog}
          aria-labelledby="alert-vote-dialog-title"
          aria-describedby="alert-vote-dialog-description"
        >
          <DialogText />
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Button onClick={handleToggleDialog}>Cancela</Button>

            <Button
              onClick={confirmVote}
              variant={"contained"}
              color={"primary"}
              autoFocus
            >
              Confirma
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={isVoteErrorDialogOpen}
          onClose={() => setVoteErrorDialogOpen(false)}
          aria-labelledby="alert-vote-error-dialog-title"
          aria-describedby="alert-vote-error-dialog-description"
        >
          <DialogTitle id="alert-vote-error-dialog-title">
            Houve um erro ao processar a avaliação
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-vote-error-dialog-description">
              Por favor, tente novamente
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "flex-end" }}>
            <Button onClick={() => setVoteErrorDialogOpen(false)}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  const ApprovalSection = () => {
    let title;
    switch (proposition.status) {
      case "SENT":
        title = "Você deseja aprovar esta proposta?";
        break;
      case "APPROVED":
        title = "Esta proposta já foi aprovada.";
        break;
      case "DENIED":
        title = "Esta proposta já foi reprovada.";
        break;
    }

    const ReturnButton = () => {
      return (
        <Button
          variant="contained"
          size="large"
          sx={{ backgroundColor: "Blue" }}
          onClick={() => navigate("/propostas")}
        >
          <Typography>Retornar</Typography>
        </Button>
      );
    };

    const YesNoButtons = () => {
      return (
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            sx={{ backgroundColor: "#00D387" }}
            onClick={handleVoteYes}
          >
            <Typography>Sim</Typography>
          </Button>
          <Button
            variant="contained"
            size="large"
            sx={{ backgroundColor: "#FF5E51" }}
            onClick={handleVoteNo}
          >
            <Typography>Não</Typography>
          </Button>
        </Box>
      );
    };

    const ApprovalSection = () => {
      if (proposition.status === "SENT") {
        return <YesNoButtons />;
      } else {
        return <ReturnButton />;
      }
    };

    return (
      <>
        <Divider variant="middle" style={{ width: "100%" }} />
        <Grid>
          <Typography fontWeight="bold">{title}</Typography>
        </Grid>

        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          gap={2}
        >
          <ApprovalSection />
        </Grid>
        <AlertDialog />
      </>
    );
  };

  return (
    <Container
      maxWidth={"lg"}
      sx={{
        minWidth: "sm",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          component="section"
          sx={[
            {
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1,
            },
          ]}
        >
          <Typography fontWeight="bold">
            Proposta #{proposition.uuid}{" "}
          </Typography>
          <Typography
            fontWeight="bold"
            sx={{
              color: "#171721",
              textTransform: "none",
              paddingLeft: 0,
            }}
            component="h1"
          >
            {proposition.title}
          </Typography>
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#535676",
              marginLeft: "-4px",
            }}
          >
            <Place fontSize="small" style={{ color: "#535676" }} />
            {proposition.location.name}
          </Typography>
          <Box
            sx={[
              {
                backgroundColor: proposition.theme.color,
                borderRadius: "16px",
                padding: "4px 10px",
                width: "fit-content",
              },
            ]}
          >
            <Typography
              sx={{
                color: "white",
                fontSize: "0.875rem",
                fontWeight: 500,
                lineHeight: 1,
              }}
            >
              <Tooltip title={proposition.theme.name} className="pressable">
                <Box
                  sx={{ display: "flex", alignItems: "center" }}
                  style={{ zIndex: -2 }}
                >
                  {proposition.theme.name}
                  <Info sx={{ fontSize: "1rem", marginLeft: "4px" }} />
                </Box>
              </Tooltip>
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Today />
            <Typography>
              Data de criação:{" "}
              {proposition.createdAt.toDate().toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </Typography>
          </Box>
        </Box>

        <Box style={{ width: "100%" }}>
          <Card variant="outlined" style={{ borderRadius: "30px" }}>
            <CardContent style={{ width: "100%", padding: 32 }}>
              <Typography>{proposition.description}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <StatusMessage status={proposition.status} />
      </Box>
      <ApprovalSection />
    </Container>
  );
};
